import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Calendar, Clock, BookOpen, ChevronRight, PlusSquare, MapPin, X, FileText, Building2, CheckSquare } from 'lucide-react';
import { formatDateBadge, todayStr } from '../utils/dateUtils';
import { SkeletonCard } from '../components/Skeleton';
import { useAuth } from '../context/AuthContext';

const formatJam = j => j ? j.slice(0, 5) : '--:--';

/* ─── Modal Jadwal Hari Ini — FULL PAGE, bisa screenshot ─── */
function TodayModal({ show, onClose, data, loading, navigate }) {
  if (!show) return null;
  const today = new Date().toLocaleDateString('id-ID', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    /* Full overlay — tidak bisa di-dismiss klik luar, harus klik tombol tutup */
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'white',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header sticky */}
      <div style={{
        padding: '20px 32px',
        background: 'linear-gradient(135deg, #1D4ED8, #2563EB)',
        color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>📅 Jadwal Hari Ini</div>
          <div style={{ fontSize: 14, opacity: 0.8, marginTop: 2 }}>{today}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, opacity: 0.8 }}>
            {data.length} agenda
          </span>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: 'white', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <X size={16} /> Tutup
          </button>
        </div>
      </div>

      {/* Konten — scroll jika banyak */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', background: '#F8FAFF' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: 'var(--gray-400)', fontSize: 15 }}>
            Memuat jadwal...
          </div>
        ) : data.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'var(--gray-400)' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>📭</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Tidak ada jadwal hari ini</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 800, margin: '0 auto' }}>
            {data.map((j, i) => (
              <div key={j.id} style={{
                background: 'white',
                borderRadius: 12,
                border: `1px solid ${j.is_gubernur ? '#F59E0B' : 'var(--gray-200)'}`,
                borderLeft: `4px solid ${j.is_gubernur ? '#F59E0B' : 'var(--primary)'}`,
                padding: '20px 24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                background: j.is_gubernur ? '#FFFBEB' : 'white',
              }}>
                {/* Nomor urut + jam */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: j.is_gubernur ? '#F59E0B' : 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, padding: '4px 12px', background: 'var(--primary-bg)', color: 'var(--primary)', borderRadius: 100, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Clock size={12} /> {formatJam(j.jam_mulai)} – {j.is_selesai_tbd ? 'Selesai' : formatJam(j.jam_selesai)}
                  </span>
                  {j.is_gubernur == 1 && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', background: '#FEF3C7', color: '#92400E', borderRadius: 100 }}>⭐ Undangan Gubernur</span>
                  )}
                  {j.is_disposisi == 1 && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', background: 'var(--secondary-bg)', color: '#065F46', borderRadius: 100, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <CheckSquare size={11} /> Disposisi
                    </span>
                  )}
                </div>

                {/* Nama acara */}
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--gray-900)', marginBottom: 10, lineHeight: 1.3 }}>
                  {j.nama_acara}
                </div>

                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <Building2 size={13} color="var(--gray-400)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>{j.penyelenggara || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <MapPin size={13} color="var(--gray-400)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>{j.lokasi || '—'}</span>
                  </div>
                </div>

                {/* No Surat */}
                {j.no_surat && (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FileText size={12} /> No. Surat: <strong>{j.no_surat}</strong>
                  </div>
                )}

                {/* Keterangan — render HTML dari Quill */}
                {j.keterangan && j.keterangan !== '<p><br></p>' && (
                  // <div
                  //   className="quill-content"
                  //   dangerouslySetInnerHTML={{ __html: j.keterangan }}
                  //   style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.7, padding: '10px 14px', background: 'rgba(0,0,0,0.03)', borderRadius: 8, marginBottom: 8, borderLeft: '3px solid var(--gray-200)' }}
                  // />
                  <div style={{ whiteSpace: "pre-line" }}>{j.keterangan}</div>
                )}

                {/* Disposisi keterangan */}
                {j.is_disposisi == 1 && j.disposisi_ket && (
                  <div style={{ marginTop: 5, fontSize: 13, color: '#065F46', padding: '8px 12px', background: 'var(--secondary-bg)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <CheckSquare size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span><strong>Disposisi:</strong> {j.disposisi_ket}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, bulanIni: 0, hariIni: 0, recent: [] });
  const [loading, setLoading] = useState(true);
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [todayAgenda, setTodayAgenda] = useState([]);
  const [loadingToday, setLoadingToday] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    api.get('/jadwal/dashboard')
      .then(res => setStats(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleOpenToday = async () => {
    setShowTodayModal(true);
    if (todayAgenda.length > 0) return;
    setLoadingToday(true);
    try {
      const res = await api.get(`/jadwal/date/${todayStr()}`);
      setTodayAgenda(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoadingToday(false); }
  };

  return (
    <div className="page-container">
      {/* Hero */}
      <div className="dashboard-hero" style={{ textAlign: 'center' }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 100, padding: '6px 14px', marginBottom: 20, fontSize: 13, fontWeight: 500, backdropFilter: 'blur(10px)' }}>
            <Building2 size={14} /> Sekretariat DPRD Provinsi DKI Jakarta
          </div>
          <p style={{ fontSize: 16, opacity: 0.85 }}>Penjadwalan Acara Pimpinan</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-3" style={{ marginBottom: 28 }}>
        {loading ? (<><SkeletonCard /><SkeletonCard /><SkeletonCard /></>) : (<>
          <div className="stat-card">
            <div>
              <div className="stat-label">Total Agenda</div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-sub">Seluruh jadwal tersimpan</div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--purple-bg)' }}><BookOpen size={24} color="var(--purple)" /></div>
          </div>
          <div className="stat-card">
            <div>
              <div className="stat-label">Bulan Ini</div>
              <div className="stat-value">{stats.bulanIni}</div>
              <div className="stat-sub">{new Date().toLocaleString('id-ID', { month: 'long', year: 'numeric' })}</div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--secondary-bg)' }}><Calendar size={24} color="var(--secondary)" /></div>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={handleOpenToday}>
            <div>
              <div className="stat-label">Hari Ini</div>
              <div className="stat-value">{stats.hariIni}</div>
              <div className="stat-sub" style={{ color: 'var(--primary)', fontWeight: 600 }}>Klik untuk lihat detail</div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--orange-bg)' }}><Clock size={24} color="var(--orange)" /></div>
          </div>
        </>)}
      </div>

      {/* Quick actions */}
      {isAdmin && (
        <div className="grid-2" style={{ marginBottom: 28 }}>
          <div className="card" style={{ cursor: 'pointer', overflow: 'hidden', position: 'relative' }} onClick={() => navigate('/jadwal')}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, background: 'var(--primary-bg)', borderRadius: '50%', opacity: 0.5 }} />
            <div className="card-body">
              <div style={{ width: 52, height: 52, background: 'var(--primary-bg)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><Calendar size={26} color="var(--primary)" /></div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Lihat Jadwal</h3>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16, lineHeight: 1.6 }}>Tampilan kalender interaktif untuk melihat seluruh jadwal kegiatan pimpinan</p>
              <span style={{ color: 'var(--primary)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Buka Kalender <ChevronRight size={16} /></span>
            </div>
          </div>
          <div className="card" style={{ cursor: 'pointer', overflow: 'hidden', position: 'relative' }} onClick={() => navigate('/input')}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, background: 'var(--secondary-bg)', borderRadius: '50%', opacity: 0.5 }} />
            <div className="card-body">
              <div style={{ width: 52, height: 52, background: 'var(--secondary-bg)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><PlusSquare size={26} color="var(--secondary)" /></div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Input Jadwal</h3>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16, lineHeight: 1.6 }}>Tambahkan agenda baru ke dalam sistem penjadwalan kegiatan pimpinan</p>
              <span style={{ color: 'var(--secondary)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Buat Jadwal Baru <ChevronRight size={16} /></span>
            </div>
          </div>
        </div>
      )}

      {/* Recent */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, background: 'var(--orange-bg)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Clock size={18} color="var(--orange)" /></div>
            <div><div style={{ fontSize: 16, fontWeight: 700 }}>Jadwal Terbaru</div><div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Agenda yang baru diinput ke sistem</div></div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/rekap')}>Lihat Semua <ChevronRight size={14} /></button>
        </div>
        <div style={{ padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Memuat data...</div>
          ) : stats.recent.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">📅</div><p>Belum ada jadwal tersimpan</p></div>
          ) : (
            stats.recent.map((j, i) => {
              const { day, month } = formatDateBadge(j.tanggal);
              return (
                <div key={j.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px', borderBottom: i < stats.recent.length - 1 ? '1px solid var(--gray-100)' : 'none', cursor: 'pointer', transition: 'background 0.15s', background: j.is_gubernur ? '#FFFBEB' : 'transparent' }}
                  onClick={() => navigate('/jadwal')}
                  onMouseEnter={e => e.currentTarget.style.background = j.is_gubernur ? '#FEF3C7' : 'var(--gray-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = j.is_gubernur ? '#FFFBEB' : 'transparent'}>
                  <div className="date-badge">
                    <div className="date-badge-day">{day}</div>
                    <div className="date-badge-month">{month}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {j.nama_acara}
                      {j.is_gubernur == 1 && <span style={{ fontSize: 10, padding: '1px 6px', background: '#FEF3C7', color: '#92400E', borderRadius: 100, fontWeight: 700 }}>Gubernur</span>}
                      {j.is_disposisi == 1 && <span style={{ fontSize: 10, padding: '1px 6px', background: 'var(--secondary-bg)', color: '#065F46', borderRadius: 100, fontWeight: 700 }}>Disposisi</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{formatJam(j.jam_mulai)} – {j.is_selesai_tbd ? 'Selesai' : formatJam(j.jam_selesai)}</span>
                      {j.lokasi && <><span>•</span><span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} />{j.lokasi}</span></>}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', textAlign: 'right' }}>{j.penyelenggara || '—'}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal Jadwal Hari Ini — Full Screen */}
      <TodayModal
        show={showTodayModal}
        onClose={() => setShowTodayModal(false)}
        data={todayAgenda}
        loading={loadingToday}
        navigate={navigate}
      />
    </div>
  );
}
